import { memo as Memo, useEffect, useRef, useCallback } from 'react';
import gsap, { Linear } from 'gsap';

//* Components
import Container from 'components/common/Container';
import Image from 'components/common/Image';
import Text from 'components/common/Text';

//* Style
import DesignStyle from './style';

const Design = Memo(({ title, description, image, bgColor = 'BigStone', first }) => {
	//! Refs
	const contRef = useRef();
	const imageParentContRef = useRef();
	const bgRef = useRef();
	const textRef1 = useRef();
	const textRef2 = useRef();
	const textRef3 = useRef();
	const textRef4 = useRef();
	const descriptionRef = useRef();
	const marqueeRef1 = useRef();
	const marqueeRef2 = useRef();
	const rollingTween = useRef(gsap.timeline());
	const time = useRef(46);
	const isAnimated = useRef(false);

	//! Rolling animation
	const startRolling = useCallback((progress) => {
		const width = textRef1.current?.getBoundingClientRect().width;

		rollingTween.current.kill();

		rollingTween.current = gsap
			.timeline()
			?.to([textRef1.current, textRef2.current, textRef3.current, textRef4.current], {
				duration: time.current,
				x: -width,
				ease: Linear.easeNone,
				repeat: -1,
			})
			.progress(progress || 0);

		return rollingTween.current;
	}, []);

	//! Resize listener
	useEffect(() => {
		isAnimated.current = false;
		setDefaultPosition();
		handleScroll();
		window.addEventListener('scroll', handleScroll);
		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('scroll', handleScroll);
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	//! Scroll function
	const handleScroll = useCallback(() => {
		const contObj = contRef.current.getBoundingClientRect();
		const startPosition = window.innerHeight * 0.8;

		if (contObj.top <= startPosition && contObj.top > 0 && !isAnimated.current) {
			isAnimated.current = true;

			gsap.killTweensOf([imageParentContRef.current, bgRef.current, marqueeRef1.current, marqueeRef2.current]);

			gsap.to(imageParentContRef.current, { scale: 1, rotate: 5, duration: 1 });
			descriptionRef.current && gsap.to(descriptionRef.current, { yPercent: 0, duration: 1 });
			gsap.to(bgRef.current, { xPercent: -50, yPercent: -50, scale: 0.7, rotate: 5, duration: 1 }).then(() => {
				gsap.to(bgRef.current, { xPercent: -50, yPercent: -50, scale: 1, rotate: 15, duration: 0.7 }).then(
					() => {
						gsap.to([marqueeRef1.current, marqueeRef2.current], {
							force3D: false,
							xPercent: -50,
							opacity: 1,
							duration: 1,
							ease: Linear.easeNone,
						}).then(() => {
							startRolling();
						});
					}
				);
			});
		}
	}, []);

	//! Setting default positions of elements
	const setDefaultPosition = useCallback(() => {
		gsap.set(imageParentContRef.current, { scale: 0, rotate: 0 });
		gsap.set(bgRef.current, { scale: 0, rotate: 0, xPercent: -50, yPercent: -50 });
		gsap.set(marqueeRef1.current, { xPercent: -50, yPercent: -50, opacity: 0 });
		gsap.set(marqueeRef2.current, { xPercent: -50, yPercent: -50, opacity: 0 });
		descriptionRef.current && gsap.set(descriptionRef.current, { yPercent: 100 });
	}, []);

	//! Resize function
	const handleResize = useCallback(() => {
		const progress = rollingTween.current.progress();
		rollingTween.current.time(0).kill();

		startRolling(progress);
	}, [startRolling]);

	return (
		<DesignStyle ref={contRef}>
			<Container
				first={first}
				full
				section>
				<div className='design'>
					<div
						ref={imageParentContRef}
						className='image-parent-cont'>
						<Image
							className={'design-image'}
							objectFit={'cover'}
							src={image.url}
							alt={image.alt}
						/>
						<div className='marquee-cont'>
							<div
								ref={marqueeRef1}
								className='marquee-inner-cont'>
								<p
									ref={textRef1}
									className={'design-text h1 h1L font-soundscape-bold primaryColor3'}
									dangerouslySetInnerHTML={{
										__html: `<span class="space">${title}</span><span class="space">${title}</span><span class="space">${title}</span><span class="space">${title}</span>`,
									}}
								/>
								<p
									ref={textRef2}
									className={'design-text h1 h1L font-soundscape-bold primaryColor3'}
									dangerouslySetInnerHTML={{
										__html: `<span class="space">${title}</span><span class="space">${title}</span><span class="space">${title}</span><span class="space">${title}</span>`,
									}}
								/>
							</div>
						</div>
					</div>

					<div
						ref={bgRef}
						className={`colored-board bg${bgColor}`}
					/>

					<div
						ref={marqueeRef2}
						className='marquee-cont'>
						<p
							ref={textRef3}
							className={`design-text h1 h1L font-soundscape-bold bg${bgColor}-text`}
							dangerouslySetInnerHTML={{
								__html: `<span class="space">${title}</span><span class="space">${title}</span><span class="space">${title}</span><span class="space">${title}</span>`,
							}}
						/>
						<p
							ref={textRef4}
							className={`design-text h1 h1L font-soundscape-bold bg${bgColor}-text`}
							dangerouslySetInnerHTML={{
								__html: `<span class="space">${title}</span><span class="space">${title}</span><span class="space">${title}</span><span class="space">${title}</span>`,
							}}
						/>
					</div>
				</div>
				{description && (
					<div className='hidden-container'>
						<Text
							tag={first ? 'h1' : 'p'}
							ref={descriptionRef}
							className={'h4 font-ping text'}
							text={description}
						/>
					</div>
				)}
			</Container>
		</DesignStyle>
	);
});

export default Design;
